// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Preferences service — read/write the caller's steering preferences.
 *
 * The preferences routes live on the same REST API as the feedback endpoint
 * (behind the Cognito authorizer), so this reuses the published `feedbackApiUrl`
 * base and builds `${base}preferences`. Auth uses the Cognito ID token, same as
 * the sessions/feedback services; the owner is derived server-side from that
 * token, so no user id is sent from the client.
 */

import type { UserPreferences } from "@/types/preferences"

// Cache the API base URL across calls (it never changes within a session).
let cachedBaseUrl = ""

/**
 * Read the REST API root from `aws-exports.json` (reused from the feedback API).
 *
 * @returns the API base URL (ending in "/").
 * @throws if the config cannot be loaded or the URL is missing.
 */
async function loadBaseUrl(): Promise<string> {
  if (cachedBaseUrl) return cachedBaseUrl
  const response = await fetch("/aws-exports.json")
  const config = await response.json()
  if (!config.feedbackApiUrl) {
    throw new Error("Preferences API URL not configured (feedbackApiUrl missing)")
  }
  cachedBaseUrl = config.feedbackApiUrl as string
  return cachedBaseUrl
}

/**
 * Fetch the caller's preferences (defaults if none are set server-side).
 *
 * @param idToken - Cognito ID token for the Authorization header.
 * @returns the user's preferences in camelCase.
 * @throws if unauthenticated or the response is not ok.
 */
export async function getPreferences(idToken: string): Promise<UserPreferences> {
  const base = await loadBaseUrl()
  const response = await fetch(`${base}preferences`, {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    throw new Error(detail.error || `HTTP ${response.status}`)
  }
  return (await response.json()) as UserPreferences
}

/**
 * Replace the caller's preferences.
 *
 * @param prefs - the full preferences document to store (camelCase).
 * @param idToken - Cognito ID token for the Authorization header.
 * @throws if unauthenticated or the response is not ok.
 */
export async function savePreferences(prefs: UserPreferences, idToken: string): Promise<void> {
  const base = await loadBaseUrl()
  const response = await fetch(`${base}preferences`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(prefs),
  })
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}))
    throw new Error(detail.error || `HTTP ${response.status}`)
  }
}
